import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { motion } from 'framer-motion';
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { FiArrowRight, FiCalendar, FiCheckSquare, FiClipboard, FiCoffee } from 'react-icons/fi';
import { DoorOpen } from 'lucide-react';

// --- 画像インポート (変更なし) ---
import appIcon from '../assets/app-icon.png';
import screenshotStudyRoom from '../assets/screenshotStudyRoom.png';
import screenshotClassBattle from '../assets/screenshotClassBattle.png';
import screenshotProfile from '../assets/screenshotProfile.png';
import developerStoryImage from '../assets/image.png';

// --- メインのHomePageコンポーネント ---
const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);

  // ▼追加: パーティクルエンジン初期化
  const particlesInit = useCallback(async engine => {
    await loadSlim(engine);
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCtaClick = () => navigate(user ? '/all-records' : '/login');

  const allInOneFeatures = [
    { icon: FiCalendar, title: '時間割', description: '学校の予定もアプリで一括管理。' },
    { icon: FiClipboard, title: '提出物管理', description: '提出忘れをなくし、計画的に。' },
    { icon: FiCheckSquare, title: 'ToDoリスト', description: '今日のやるべきことを明確に。' },
    { icon: FiCoffee, title: '学習のヒント', description: 'ローディング中に役立つ豆知識。' },
  ];

  return (
    <div className="bg-slate-900 text-slate-100 font-sans antialiased">
      {/* ▼変更点: 動的なパーティクル背景を追加 */}
      <Particles
        id="tsparticles"
        init={particlesInit}
        options={{
          background: { color: { value: "#0f172a" } }, // slate-900
          fpsLimit: 60,
          interactivity: { events: { onHover: { enable: true, mode: "repulse" }, resize: true }, modes: { repulse: { distance: 60, duration: 0.4 } } },
          particles: {
            color: { value: "#ffffff" },
            links: { color: "#ffffff", distance: 150, enable: true, opacity: 0.1, width: 1 },
            move: { direction: "none", enable: true, outModes: { default: "bounce" }, random: true, speed: 1, straight: false },
            number: { density: { enable: true, area: 800 }, value: 50 },
            opacity: { value: 0.2 },
            shape: { type: "circle" },
            size: { value: { min: 1, max: 3 } },
          },
          detectRetina: true,
        }}
        className="fixed top-0 left-0 w-full h-full z-0"
      />
      
      <div className="relative z-10">
        {/* ▼変更点: グラスモーフィズム効果をヘッダーに適用 */}
        <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-slate-900/50 backdrop-blur-xl shadow-lg' : 'bg-transparent'}`}>
          <div className="container mx-auto px-6 py-4 flex justify-between items-center">
            <div className="flex items-center">
              <img src={appIcon} alt="StudyQuest App Icon" className="h-8 w-8 mr-3"/>
              <h1 className="text-2xl font-bold text-white">StudyQuest</h1>
            </div>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={handleCtaClick} className="group flex items-center px-5 py-2 bg-indigo-500 text-white font-semibold rounded-lg shadow-lg hover:bg-indigo-600 transition-all">
              {user ? 'アプリを開く' : 'ログイン'}
              <FiArrowRight className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
            </motion.button>
          </div>
        </header>

        <main>
          {/* Hero Section */}
          <section className="relative pt-48 pb-32 sm:pt-56 sm:pb-40 text-center overflow-hidden">
            <div className="container mx-auto px-6">
              <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
                <img src={appIcon} alt="StudyQuest App Icon" className="h-24 w-24 mx-auto mb-8 shadow-2xl rounded-3xl"/>
              </motion.div>
              {/* ▼変更点: タイポグラフィとカラーパレットを強化 */}
              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }}
                className="text-5xl sm:text-7xl font-black mb-6 leading-tight bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 bg-clip-text text-transparent">
                学習を、冒険に変えよう。
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8 }}
                className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto mb-12">
                日々の勉強が、仲間と挑むクエストになる。
                StudyQuestは、あなたの学習を最高にエキサイティングな体験に変えるプラットフォームです。
              </motion.p>
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.6, duration: 0.5 }}>
                 {/* ▼変更点: 3Dホバーエフェクトとシャドウを強化 */}
                <motion.button whileHover={{ scale: 1.1, y: -5, transition: { type: 'spring', stiffness: 300 } }} onClick={handleCtaClick}
                  className="group inline-flex items-center py-4 px-10 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white font-bold text-lg rounded-full shadow-2xl shadow-purple-500/30 hover:shadow-purple-500/50 transition-shadow">
                  {user ? '冒険を続ける' : '招待された方はこちら'}
                  <FiArrowRight className="ml-3 transition-transform duration-300 group-hover:translate-x-1.5" />
                </motion.button>

                {/* 新しく追加するボタン */}
                {user && (
                  <motion.button
                    whileHover={{ scale: 1.05, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/study-room')}
                    className="mt-4 px-6 py-3 bg-white/10 text-white border border-white/30 rounded-full text-md font-semibold shadow-md hover:bg-white/20 transition-all flex items-center justify-center mx-auto"
                  >
                    <DoorOpen className="mr-2" size={18} />
                    自習室へ直行
                  </motion.button>
                )}
              </motion.div>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.8 }}
                className="text-sm text-slate-500 max-w-3xl mx-auto mt-8">
                現在、本アプリは登録済みの学校の生徒様のみご利用いただけます。
              </motion.p>
            </div>
          </section>

          {/* ▼変更点: 全セクションに大きな間隔、カードに大きな角丸と3Dホバーを追加 */}
          {[
            { type: 'story', image: developerStoryImage, title: 'ひとりの「悩み」から、<br/>このアプリは生まれました。', description: '「勉強を続けるのが難しい…」「既存のアプリには、本当に欲しい機能がない」。開発者も、皆さんと同じ現役の学生です。このシンプルな悩みを原点に、生成AIを相棒として、試行錯誤の末にStudyQuestは開発されました。', alt: '開発風景', order: 'imageFirst' },
            { type: 'feature', image: screenshotProfile, title: '努力を、可視化する。', description: '日々の学習時間、科目ごとのバランス、連続学習日数。あなたの頑張りを美しいグラフやカレンダーで直感的に振り返ることができます。自分の成長が目に見えるから、次の一歩が踏み出しやすくなります。', alt: 'プロフィール画面', order: 'textFirst' },
            { type: 'feature', image: screenshotClassBattle, title: 'ライバルが、仲間に変わる。', description: '個人ランキングでの競争はもちろん、クラス一丸となって戦う「クラス対抗バトル」はStudyQuestの醍醐味。クラスへの貢献度がモチベーションを刺激し、一体感を育みます。', alt: 'クラス対抗バトル画面', order: 'imageFirst' },
            { type: 'feature', image: screenshotStudyRoom, title: '離れていても、一緒に集中。', description: 'バーチャル自習室に参加すれば、そこは仲間と集う学びの空間。チャットで励まし合ったり、ポモドーロタイマーで集中力を高めたり。一人じゃない感覚が、あなたの学習を後押しします。', alt: '自習室画面', order: 'textFirst' },
          ].map((section, index) => (
            <section key={index} className={`py-32 sm:py-40 overflow-hidden ${index % 2 === 1 ? 'bg-slate-900' : 'bg-slate-800/50'}`}>
              <div className="container mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
                <motion.div initial={{ opacity: 0, x: section.order === 'imageFirst' ? -50 : 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.8 }}
                  className={`${section.order === 'imageFirst' ? 'md:order-1' : 'md:order-2'}`}>
                  <motion.div whileHover={{ scale: 1.05, rotateY: -5, rotateX: 2, transition: { type: 'spring', stiffness: 200 } }}
                    className="relative p-2 rounded-3xl bg-gradient-to-br from-indigo-500/20 to-pink-500/20 shadow-2xl [transform-style:preserve-3d]">
                    <img src={section.image} alt={section.alt} className="rounded-2xl shadow-lg"/>
                  </motion.div>
                </motion.div>
                <motion.div initial={{ opacity: 0, x: section.order === 'imageFirst' ? 50 : -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.8 }}
                  className={`${section.order === 'imageFirst' ? 'md:order-2' : 'md:order-1'}`}>
                  <h2 className="text-4xl sm:text-5xl font-black mb-6" dangerouslySetInnerHTML={{ __html: section.title }} />
                  <p className="text-lg text-slate-300">{section.description}</p>
                </motion.div>
              </div>
            </section>
          ))}
          
          <section className="py-32 sm:py-40 bg-slate-900">
            <div className="container mx-auto px-6">
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.8 }}
                className="text-center max-w-3xl mx-auto mb-20">
                <h2 className="text-4xl sm:text-5xl font-black mb-4">学習の「面倒」も、このアプリ一つで。</h2>
                <p className="text-lg text-slate-300">StudyQuestは記録や競争だけでなく、日々の学校生活もスマートにサポートします。</p>
              </motion.div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {allInOneFeatures.map((feature, index) => (
                  <motion.div key={feature.title} initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.5 }} transition={{ delay: index * 0.1, duration: 0.6 }}
                    className="relative p-8 rounded-3xl bg-white/10 backdrop-blur-lg border border-white/20 text-center transition-all duration-300 hover:bg-white/20 hover:-translate-y-3 hover:shadow-2xl">
                    <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 text-white shadow-lg">
                      <feature.icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-white">{feature.title}</h3>
                    <p className="text-slate-300">{feature.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-32">
            <div className="container mx-auto px-6">
              <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-3xl shadow-2xl p-12 text-center overflow-hidden">
                <div className="absolute inset-0 bg-black/20 mix-blend-multiply"></div>
                <div className="relative z-10">
                  <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">さあ、学習の冒険を始めよう</h2>
                  <p className="text-lg text-indigo-100 max-w-2xl mx-auto mb-10">
                    {user ? '仲間と共に、さらなる高みを目指そう。' : '招待された生徒の皆さんは、ログインしてStudyQuestの世界へ。'}
                  </p>
                  <motion.button whileHover={{ scale: 1.1, y: -5, transition: { type: 'spring', stiffness: 300 } }} onClick={handleCtaClick}
                    className="group inline-flex items-center py-4 px-10 bg-white text-indigo-600 font-bold text-lg rounded-full shadow-2xl hover:shadow-indigo-200/50 transition-shadow">
                    {user ? 'アプリを開く' : 'ログインはこちら'}
                    <FiArrowRight className="ml-3 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </motion.button>
                </div>
              </div>
            </div>
          </section>
        </main>

        <footer className="bg-transparent text-slate-500">
          <div className="container mx-auto px-6 py-12 text-center">
            <p>&copy; {new Date().getFullYear()} StudyQuest. All Rights Reserved.</p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default HomePage;